/* Dashboard Builder — tile catalog + renderer.
 *
 * Each catalog entry maps a tile ``type`` to a data source and the chart
 * variants it supports. ``TileView`` renders a single tile instance using the
 * shared DashboardData context and the active colour palette. Tiles fill their
 * grid cell (h-full) so react-grid-layout controls their size. */
import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
  ScatterChart, Scatter, LineChart, Line, AreaChart, Area,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Layers, Wallet, TrendingUp, AlertTriangle, Users, Activity, ShieldAlert,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useDashData } from './DashboardData';
import { BandColors, TileChart, TileInstance } from './types';

export interface CatalogEntry {
  key: string;
  label: string;
  description: string;
  group: 'KPIs' | 'Risk' | 'Portfolio' | 'Geography' | 'Operations' | 'Custom';
  charts?: TileChart[];        // selectable variants (first = default)
  metrics?: Array<{ key: string; label: string }>;
  custom?: boolean;            // uses the custom-chart query builder
  defaultSize: { w: number; h: number };
}

export const TILE_CATALOG: CatalogEntry[] = [
  { key: 'custom', label: 'Custom chart', description: 'Build your own: choose a dataset, X/Y, series and chart type.', group: 'Custom', custom: true, defaultSize: { w: 6, h: 4 } },
  { key: 'operational_kpis', label: 'Operational KPIs', description: 'Brokers, approved staff and access-event counters.', group: 'KPIs', defaultSize: { w: 12, h: 2 } },
  {
    key: 'kpi', label: 'Single KPI', description: 'One headline number of your choosing.', group: 'KPIs',
    metrics: [
      { key: 'sites', label: 'Sites assessed' },
      { key: 'total_cash', label: 'Total cash exposure' },
      { key: 'avg_cash', label: 'Avg cash / site' },
      { key: 'avg_score', label: 'Avg risk score' },
      { key: 'high_critical', label: 'High / Critical count' },
    ],
    defaultSize: { w: 3, h: 2 },
  },
  { key: 'risk_distribution', label: 'Risk distribution', description: 'Share of the portfolio in each risk band.', group: 'Risk', charts: ['donut', 'pie', 'bar'], defaultSize: { w: 4, h: 4 } },
  { key: 'cash_vs_score', label: 'Cash vs risk score', description: 'One dot per site — exposure against score.', group: 'Risk', charts: ['scatter'], defaultSize: { w: 6, h: 4 } },
  { key: 'abm_types', label: 'ABM type analysis', description: 'Risk bands stacked per ABM type.', group: 'Risk', charts: ['stacked'], defaultSize: { w: 6, h: 4 } },
  { key: 'worst_categories', label: 'Worst answer categories', description: 'Categories most often answered poorly.', group: 'Risk', charts: ['hbar'], defaultSize: { w: 6, h: 4 } },
  { key: 'security_focus', label: 'Security pass rate', description: 'Security-section pass rate + top failing questions.', group: 'Risk', charts: ['number'], defaultSize: { w: 6, h: 4 } },
  { key: 'top_sites', label: 'Top risk sites', description: 'Highest-risk sites ranked by score.', group: 'Risk', charts: ['table'], defaultSize: { w: 6, h: 4 } },
  { key: 'broker_portfolio', label: 'Broker portfolio', description: 'Risk bands per broker.', group: 'Portfolio', charts: ['stacked', 'table'], defaultSize: { w: 6, h: 4 } },
  { key: 'template_portfolio', label: 'Template portfolio', description: 'Risk bands per survey template.', group: 'Portfolio', charts: ['stacked'], defaultSize: { w: 6, h: 4 } },
  { key: 'outstanding', label: 'Outstanding actions', description: 'Overdue, unrated and high-risk counts.', group: 'Operations', charts: ['list'], defaultSize: { w: 4, h: 4 } },
  { key: 'site_map', label: 'Site location map', description: 'Geographic view of every rated site.', group: 'Geography', charts: ['map'], defaultSize: { w: 6, h: 5 } },
];

export const catalogByKey = (k: string): CatalogEntry | undefined => TILE_CATALOG.find((c) => c.key === k);

const cap = (b: string) => (b ? b.charAt(0).toUpperCase() + b.slice(1).toLowerCase() : b);
const bandColor = (colors: BandColors, band: string): string => {
  const key = cap(band) as keyof BandColors;
  return (colors[key] as string) || '#94A3B8';
};
const BANDS = ['Low', 'Medium', 'High', 'Critical'] as const;

const Empty: React.FC<{ msg?: string }> = ({ msg }) => (
  <div className="w-full h-full grid place-items-center text-xs text-raptor-navy/40 font-body p-3 text-center">
    {msg || 'No data yet.'}
  </div>
);

/* ─── Individual renderers ─────────────────────────────────────────── */
const KpiBig: React.FC<{ label: string; value: string | number; icon: React.ReactNode; accent?: string }>
  = ({ label, value, icon, accent }) => (
  <div className="w-full h-full flex flex-col justify-center px-4">
    <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 inline-flex items-center gap-1.5">{icon}{label}</div>
    <div className={`font-tabular text-3xl font-bold mt-1 ${accent || 'text-raptor-navy'}`}>{value}</div>
  </div>
);

const OperationalKpis: React.FC = () => {
  const { ops } = useDashData();
  const cells = [
    { label: 'Brokers', value: ops?.brokers ?? 0, icon: <Users size={13} /> },
    { label: 'Approved staff', value: ops?.staff_approved ?? 0, icon: <Users size={13} /> },
    { label: 'Access (24h)', value: ops?.access_24h ?? 0, icon: <Activity size={13} /> },
    { label: 'Access (7d)', value: ops?.access_7d ?? 0, icon: <Activity size={13} /> },
    { label: 'Clients', value: ops?.clients ?? 0, icon: <Layers size={13} /> },
    { label: 'Sites', value: ops?.sites ?? 0, icon: <Layers size={13} /> },
  ];
  return (
    <div className="w-full h-full grid grid-cols-3 sm:grid-cols-6 gap-2 p-3">
      {cells.map((c) => (
        <div key={c.label} className="rounded-md bg-raptor-neutral/30 px-2 py-2 flex flex-col justify-center">
          <div className="text-[9px] uppercase text-raptor-navy/55 inline-flex items-center gap-1">{c.icon}{c.label}</div>
          <div className="font-tabular text-xl font-bold text-raptor-navy">{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const SingleKpi: React.FC<{ metric: string }> = ({ metric }) => {
  const { insights, fmtMoney } = useDashData();
  const k = insights?.kpis;
  if (!k) return <Empty />;
  switch (metric) {
    case 'total_cash': return <KpiBig label="Total cash exposure" value={fmtMoney(k.total_cash_exposure)} icon={<Wallet size={13} />} />;
    case 'avg_cash': return <KpiBig label="Avg cash / site" value={fmtMoney(k.avg_cash_exposure)} icon={<Wallet size={13} />} />;
    case 'avg_score': return <KpiBig label="Avg risk score" value={(k.avg_risk_score ?? 0).toFixed(1)} icon={<TrendingUp size={13} />} />;
    case 'high_critical': return <KpiBig label="High / Critical" value={`${k.high_critical_count} · ${k.high_critical_pct.toFixed(0)}%`} icon={<AlertTriangle size={13} />} accent="text-red-600" />;
    default: return <KpiBig label="Sites assessed" value={k.site_count} icon={<Layers size={13} />} />;
  }
};

const RiskDistribution: React.FC<{ chart: TileChart; colors: BandColors }> = ({ chart, colors }) => {
  const { insights } = useDashData();
  const data = insights?.risk_distribution || [];
  if (data.length === 0) return <Empty msg="No rated sites yet." />;
  if (chart === 'bar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="band" style={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} style={{ fontSize: 11 }} />
          <RTooltip />
          <Bar dataKey="count" isAnimationActive={false} radius={[4, 4, 0, 0]}>
            {data.map((e) => <Cell key={e.band} fill={bandColor(colors, e.band)} />)}
            <LabelList dataKey="count" position="top" style={{ fontSize: 11 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 4, right: 4, bottom: 20, left: 4 }}>
        <Pie data={data as unknown as Array<Record<string, unknown>>} dataKey="count" nameKey="band" cx="50%" cy="48%"
             innerRadius={chart === 'donut' ? '55%' : 0} outerRadius="85%" paddingAngle={2} isAnimationActive={false}>
          {data.map((e) => <Cell key={e.band} fill={bandColor(colors, e.band)} />)}
        </Pie>
        <RTooltip />
        <Legend verticalAlign="bottom" height={20} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const CashVsScore: React.FC<{ colors: BandColors }> = ({ colors }) => {
  const { insights, fmtMoney, sym } = useDashData();
  const scatter = insights?.scatter || [];
  if (scatter.length === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={{ top: 8, right: 16, bottom: 28, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="x" type="number" tickFormatter={(v: number) => fmtMoney(v)} style={{ fontSize: 10 }} label={{ value: `Cash (${sym})`, position: 'insideBottom', offset: -6, style: { fontSize: 10 } }} />
        <YAxis dataKey="y" type="number" style={{ fontSize: 10 }} />
        <RTooltip formatter={((v: number, n: string) => (n === 'x' ? fmtMoney(v) : v)) as never} />
        {BANDS.map((b) => (
          <Scatter key={b} name={b} data={scatter.filter((p) => cap(p.band) === b) as unknown as object[]} fill={colors[b]} isAnimationActive={false} />
        ))}
        <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 10, paddingTop: 6 }} />
      </ScatterChart>
    </ResponsiveContainer>
  );
};

const AbmTypes: React.FC<{ colors: BandColors }> = ({ colors }) => {
  const { insights } = useDashData();
  const data = insights?.abm_types || [];
  if (data.length === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 8, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" style={{ fontSize: 10 }} />
        <YAxis dataKey="abm_type" type="category" width={110} style={{ fontSize: 10 }} />
        <RTooltip />
        <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 10 }} />
        {BANDS.map((b) => <Bar key={b} dataKey={b} stackId="a" fill={colors[b]} isAnimationActive={false} />)}
      </BarChart>
    </ResponsiveContainer>
  );
};

const WorstCategories: React.FC<{ colors: BandColors }> = ({ colors }) => {
  const { insights } = useDashData();
  const data = insights?.worst_categories || [];
  if (data.length === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ left: 12, right: 32, top: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" style={{ fontSize: 10 }} />
        <YAxis dataKey="category" type="category" width={150} style={{ fontSize: 10 }} />
        <RTooltip />
        <Bar dataKey="flagged_count" fill={colors.High} radius={[0, 4, 4, 0]} isAnimationActive={false}>
          <LabelList dataKey="flagged_count" position="right" style={{ fontSize: 10 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const SecurityFocus: React.FC<{ colors: BandColors }> = ({ colors }) => {
  const { insights } = useDashData();
  const sf = insights?.security_focus;
  if (!sf) return <Empty />;
  return (
    <div className="w-full h-full flex flex-col p-3 gap-2">
      <div className="shrink-0">
        <div className="text-[10px] uppercase text-raptor-navy/60">Security pass rate</div>
        <div className="font-tabular text-2xl font-bold text-raptor-navy">{(sf.pass_rate_pct ?? 0).toFixed(1)}%</div>
        <div className="text-[10px] text-raptor-navy/60">{sf.totals.yes} Yes · {sf.totals.no} No · {sf.totals.na} N/A</div>
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sf.per_question} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" style={{ fontSize: 9 }} />
            <YAxis dataKey="question_code" type="category" width={150} style={{ fontSize: 9 }} />
            <RTooltip />
            <Bar dataKey="yes" stackId="s" fill={colors.Low} isAnimationActive={false} />
            <Bar dataKey="no" stackId="s" fill={colors.High} isAnimationActive={false} />
            <Bar dataKey="na" stackId="s" fill="#94A3B8" isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const TopSites: React.FC<{ colors: BandColors }> = ({ colors }) => {
  const { insights, fmtMoney } = useDashData();
  const rows = insights?.top_sites || [];
  if (rows.length === 0) return <Empty />;
  return (
    <div className="w-full h-full overflow-auto">
      <table className="w-full text-xs font-body">
        <thead className="bg-raptor-neutral/40 text-[10px] uppercase text-raptor-navy/70 sticky top-0">
          <tr><th className="py-1.5 px-2 text-left">Site</th><th className="py-1.5 px-2 text-left">Band</th><th className="py-1.5 px-2 text-right">Score</th><th className="py-1.5 px-2 text-right">Cash</th></tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={s.site_id || i} className="border-t border-border/40">
              <td className="py-1.5 px-2 font-semibold text-raptor-navy truncate max-w-[180px]">{s.site_name}</td>
              <td className="py-1.5 px-2"><span className="px-1.5 py-0.5 rounded text-white text-[10px]" style={{ background: bandColor(colors, s.band) }}>{s.band}</span></td>
              <td className="py-1.5 px-2 font-tabular text-right">{s.score.toFixed(1)}</td>
              <td className="py-1.5 px-2 font-tabular text-right">{fmtMoney(s.cash_exposure)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const lc = (b: string) => b.toLowerCase();
const BrokerPortfolio: React.FC<{ chart: TileChart; colors: BandColors }> = ({ chart, colors }) => {
  const { distribution } = useDashData();
  const rows = distribution?.by_broker || [];
  if (rows.length === 0) return <Empty />;
  if (chart === 'table') {
    return (
      <div className="w-full h-full overflow-auto">
        <table className="w-full text-xs font-body">
          <thead className="bg-raptor-neutral/40 text-[10px] uppercase text-raptor-navy/70 sticky top-0">
            <tr><th className="py-1.5 px-2 text-left">Broker</th>{BANDS.map((b) => <th key={b} className="py-1.5 px-2 text-right">{b}</th>)}<th className="py-1.5 px-2 text-right">Total</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.broker_id} className="border-t border-border/40">
                <td className="py-1.5 px-2 font-semibold text-raptor-navy truncate max-w-[160px]">{r.brokerage_name}</td>
                {BANDS.map((b) => <td key={b} className="py-1.5 px-2 font-tabular text-right">{(r as unknown as Record<string, number>)[lc(b)]}</td>)}
                <td className="py-1.5 px-2 font-tabular text-right font-semibold">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  const data = rows.map((r) => ({ name: r.brokerage_name, Low: r.low, Medium: r.medium, High: r.high, Critical: r.critical }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 8, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" style={{ fontSize: 10 }} />
        <YAxis dataKey="name" type="category" width={120} style={{ fontSize: 9 }} />
        <RTooltip />
        <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 10 }} />
        {BANDS.map((b) => <Bar key={b} dataKey={b} stackId="a" fill={colors[b]} isAnimationActive={false} />)}
      </BarChart>
    </ResponsiveContainer>
  );
};

const TemplatePortfolio: React.FC<{ colors: BandColors }> = ({ colors }) => {
  const { distribution } = useDashData();
  const rows = distribution?.by_template || [];
  if (rows.length === 0) return <Empty />;
  const data = rows.map((r) => ({ name: r.template_name, Low: r.low, Medium: r.medium, High: r.high, Critical: r.critical }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 8, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" style={{ fontSize: 10 }} />
        <YAxis dataKey="name" type="category" width={120} style={{ fontSize: 9 }} />
        <RTooltip />
        <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 10 }} />
        {BANDS.map((b) => <Bar key={b} dataKey={b} stackId="a" fill={colors[b]} isAnimationActive={false} />)}
      </BarChart>
    </ResponsiveContainer>
  );
};

const OutstandingTile: React.FC = () => {
  const { outstanding } = useDashData();
  if (!outstanding) return <Empty />;
  const items = [
    { label: 'Overdue instructions', value: outstanding.overdue_instructions.count, icon: <AlertTriangle size={15} className="text-amber-600" /> },
    { label: 'Unrated results', value: outstanding.unrated_results.count, icon: <ShieldAlert size={15} className="text-raptor-blue" /> },
    { label: 'High-risk assessments', value: outstanding.high_risk_assessments.count, icon: <AlertTriangle size={15} className="text-red-600" /> },
  ];
  return (
    <div className="w-full h-full flex flex-col gap-2 p-3">
      {items.map((it) => (
        <div key={it.label} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
          <div className="flex items-center gap-2"><span>{it.icon}</span><span className="text-xs font-semibold text-raptor-navy">{it.label}</span></div>
          <span className="font-tabular text-xl font-bold text-raptor-navy">{it.value}</span>
        </div>
      ))}
    </div>
  );
};

const SiteMap: React.FC<{ colors: BandColors }> = ({ colors }) => {
  const { insights } = useDashData();
  const pts = (insights?.map_points || []).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  return (
    <div className="w-full h-full">
      <MapContainer preferCanvas center={[-29.0, 24.5]} zoom={5} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
        {pts.map((p, i) => (
          <CircleMarker key={`${p.lat}-${p.lng}-${i}`} center={[p.lat, p.lng]} radius={6}
            pathOptions={{ color: bandColor(colors, p.band), fillColor: bandColor(colors, p.band), fillOpacity: 0.65, weight: 1.5 }} />
        ))}
      </MapContainer>
    </div>
  );
};

/* ─── Custom chart (query builder) ─────────────────────────────────── */
const CUSTOM_PALETTE = ['#0D63FF', '#36EBFE', '#7C3AED', '#F59E0B', '#10B981', '#EF4444', '#EC4899', '#14B8A6', '#6366F1', '#F97316'];
const seriesColor = (name: string, idx: number, colors: BandColors): string => {
  const c = cap(name);
  if (['Low', 'Medium', 'High', 'Critical'].includes(c)) return (colors[c as keyof BandColors] as string);
  if (name === 'value') return colors.accent;
  return CUSTOM_PALETTE[idx % CUSTOM_PALETTE.length];
};

interface AggResp {
  mode: 'category' | 'scatter' | 'number';
  chart?: string; x_label?: string; y_label?: string;
  series?: string[]; has_series?: boolean;
  rows?: Array<Record<string, unknown>>;
  points?: Array<{ x: number; y: number; band: string; label: string }>;
  value?: number; count?: number;
}

const CustomTile: React.FC<{ tile: TileInstance; colors: BandColors }> = ({ tile, colors }) => {
  const { filters, fmtMoney } = useDashData();
  const q = tile.query;
  const [data, setData] = useState<AggResp | null>(null);
  const [loading, setLoading] = useState(true);
  const fkey = JSON.stringify({ q, filters });
  useEffect(() => {
    if (!q) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    const tplIds = (q.template_ids && q.template_ids.length)
      ? q.template_ids : (filters.template_id ? [filters.template_id] : []);
    const body = {
      chart: q.chart, dimension: q.dimension, measure: q.measure, series: q.series,
      x_measure: q.x_measure, y_measure: q.y_measure, limit: q.limit, sort: q.sort,
      filters: {
        broker_id: filters.broker_id, client_id: filters.client_id, site_id: filters.site_id,
        from: filters.from, to: filters.to, template_ids: tplIds,
      },
    };
    api.post<AggResp>('/analytics/aggregate', body)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setData(null); setLoading(false); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fkey]);

  if (!q) return <Empty msg="Not configured." />;
  if (loading) return <div className="w-full h-full grid place-items-center text-xs text-raptor-navy/40">Loading…</div>;
  if (!data) return <Empty msg="No data for this query." />;
  const isMoney = q.measure === 'sum_cash' || q.measure === 'avg_cash';
  const fmtV = (v: number) => (isMoney ? fmtMoney(v) : String(v));

  if (data.mode === 'number') {
    return (
      <div className="w-full h-full flex flex-col justify-center px-4">
        <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60">{data.y_label}</div>
        <div className="font-tabular text-3xl font-bold mt-1 text-raptor-navy">{fmtV(data.value || 0)}</div>
        <div className="text-[10px] text-raptor-navy/50 mt-0.5">{data.count} surveys</div>
      </div>
    );
  }

  if (data.mode === 'scatter') {
    const pts = data.points || [];
    if (pts.length === 0) return <Empty />;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 8, right: 16, bottom: 28, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="x" type="number" style={{ fontSize: 10 }} label={{ value: data.x_label, position: 'insideBottom', offset: -6, style: { fontSize: 10 } }} />
          <YAxis dataKey="y" type="number" style={{ fontSize: 10 }} />
          <RTooltip />
          {BANDS.map((b) => (
            <Scatter key={b} name={b} data={pts.filter((p) => cap(p.band) === b) as unknown as object[]} fill={colors[b]} isAnimationActive={false} />
          ))}
          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 10, paddingTop: 6 }} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  const rows = data.rows || [];
  const series = data.series || ['value'];
  if (rows.length === 0) return <Empty />;

  if (q.chart === 'table') {
    return (
      <div className="w-full h-full overflow-auto">
        <table className="w-full text-xs font-body">
          <thead className="bg-raptor-neutral/40 text-[10px] uppercase text-raptor-navy/70 sticky top-0">
            <tr><th className="py-1.5 px-2 text-left">{data.x_label}</th>{series.map((s) => <th key={s} className="py-1.5 px-2 text-right">{s === 'value' ? data.y_label : s}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="py-1.5 px-2 font-semibold text-raptor-navy truncate max-w-[180px]">{String(r.key)}</td>
                {series.map((s) => <td key={s} className="py-1.5 px-2 font-tabular text-right">{fmtV(Number(r[s] || 0))}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (q.chart === 'pie' || q.chart === 'donut') {
    const pieData = rows.map((r) => ({ name: String(r.key), value: series.reduce((sum, s) => sum + Number(r[s] || 0), 0) }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 4, right: 4, bottom: 20, left: 4 }}>
          <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="48%" innerRadius={q.chart === 'donut' ? '55%' : 0} outerRadius="85%" paddingAngle={2} isAnimationActive={false}>
            {pieData.map((e, i) => <Cell key={e.name} fill={seriesColor(e.name, i, colors)} />)}
          </Pie>
          <RTooltip formatter={((v: number) => fmtV(v)) as never} />
          <Legend verticalAlign="bottom" height={20} iconType="circle" wrapperStyle={{ fontSize: 10 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (q.chart === 'line' || q.chart === 'area') {
    const Chart = q.chart === 'area' ? AreaChart : LineChart;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={rows} margin={{ top: 8, right: 16, bottom: 20, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="key" style={{ fontSize: 10 }} />
          <YAxis tickFormatter={isMoney ? ((v: number) => fmtMoney(v)) : undefined} style={{ fontSize: 10 }} />
          <RTooltip formatter={((v: number) => fmtV(v)) as never} />
          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 10 }} />
          {series.map((s, i) => (q.chart === 'area'
            ? <Area key={s} dataKey={s} name={s === 'value' ? data.y_label : s} stroke={seriesColor(s, i, colors)} fill={seriesColor(s, i, colors)} fillOpacity={0.25} isAnimationActive={false} />
            : <Line key={s} dataKey={s} name={s === 'value' ? data.y_label : s} stroke={seriesColor(s, i, colors)} dot={false} strokeWidth={2} isAnimationActive={false} />))}
        </Chart>
      </ResponsiveContainer>
    );
  }

  // bar + stacked
  const stack = q.chart === 'stacked';
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 20, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="key" style={{ fontSize: 10 }} />
        <YAxis tickFormatter={isMoney ? ((v: number) => fmtMoney(v)) : undefined} style={{ fontSize: 10 }} />
        <RTooltip formatter={((v: number) => fmtV(v)) as never} />
        <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 10 }} />
        {series.map((s, i) => (
          <Bar key={s} dataKey={s} name={s === 'value' ? data.y_label : s} stackId={stack ? 'a' : undefined} fill={seriesColor(s, i, colors)} radius={stack ? undefined : [3, 3, 0, 0]} isAnimationActive={false}>
            {series.length === 1 && rows.map((r, ci) => <Cell key={ci} fill={seriesColor(String(r.key), ci, colors)} />)}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

/* ─── Dispatcher ───────────────────────────────────────────────────── */
const TileViewInner: React.FC<{ tile: TileInstance; colors: BandColors }> = ({ tile, colors }) => {
  const chart = tile.chart || catalogByKey(tile.type)?.charts?.[0] || 'number';
  switch (tile.type) {
    case 'custom': return <CustomTile tile={tile} colors={colors} />;
    case 'operational_kpis': return <OperationalKpis />;
    case 'kpi': return <SingleKpi metric={tile.metric || 'sites'} />;
    case 'risk_distribution': return <RiskDistribution chart={chart} colors={colors} />;
    case 'cash_vs_score': return <CashVsScore colors={colors} />;
    case 'abm_types': return <AbmTypes colors={colors} />;
    case 'worst_categories': return <WorstCategories colors={colors} />;
    case 'security_focus': return <SecurityFocus colors={colors} />;
    case 'top_sites': return <TopSites colors={colors} />;
    case 'broker_portfolio': return <BrokerPortfolio chart={chart} colors={colors} />;
    case 'template_portfolio': return <TemplatePortfolio colors={colors} />;
    case 'outstanding': return <OutstandingTile />;
    case 'site_map': return <SiteMap colors={colors} />;
    default: return <Empty msg="Unknown tile." />;
  }
};

// Memoised on the fields that actually change what a tile DRAWS — so editing a
// tile's title or the board name (high-frequency keystrokes) no longer forces
// every chart to re-render/refetch.
export const TileView = React.memo(TileViewInner, (prev, next) => (
  prev.tile.type === next.tile.type
  && prev.tile.chart === next.tile.chart
  && prev.tile.metric === next.tile.metric
  && JSON.stringify(prev.tile.query) === JSON.stringify(next.tile.query)
  && prev.colors === next.colors
));
